import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import {
  ModalContainer,
  ContentModalContainer,
} from "../../../../../shared/components/modals";
import type {
  Product,
  Store,
  StockUpdateItem,
  MarketplaceWarehouse,
} from "../../types/ProductListing";
import {
  WarehouseService,
  ProductListingService,
  ProcessTrackingService,
} from "../../services";
import { formatVariantName } from "../../utils/variantUtils";
import BatchStockUpdate from "./components/BatchStockUpdate";
import DesktopStockUpdateView from "./components/DesktopStockUpdateView";
import MobileStockUpdateView from "./components/MobileStockUpdateView";

export interface StockUpdate {
  variantId: string;
  warehouseId: string;
  newStock: number;
}

export interface WarehouseStock {
  warehouseId: string;
  warehouseName: string;
  currentStock: number;
  readyStock: number;
}

export interface VariantStock {
  variantId: string;
  variantName: string;
  sku: string;
  merchantSku?: string;
  marketplaceSku?: string;
  image?: string;
  warehouses: WarehouseStock[];
}

interface StockUpdateModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: Product | null;
  onSave: (stockUpdates: StockUpdate[]) => Promise<void>;
  mode?: "modal" | "content";
  stores: Store[];
}

const StockUpdateModal: React.FC<StockUpdateModalProps> = ({
  isOpen,
  onClose,
  product,
  onSave,
  mode = "modal",
  stores,
}) => {
  // Service references
  const warehouseService = useRef(WarehouseService.getInstance());
  const productListingService = useRef(ProductListingService.getInstance());
  const processTrackingService = useRef(ProcessTrackingService.getInstance());

  // State
  const [variants, setVariants] = useState<VariantStock[]>([]);
  const [warehouses, setWarehouses] = useState<MarketplaceWarehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [warehouseLoading, setWarehouseLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processId, setProcessId] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  // Mobile viewport detection
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Fetch warehouses function (can be called manually or on mount)
  const fetchWarehouses = async (forceRefresh = false) => {
    if (!product) return;

    try {
      setWarehouseLoading(true);
      setError(null);

      // Clear cache if force refresh requested
      if (forceRefresh) {
        console.log(
          "[StockUpdateModal] Force refreshing warehouses - clearing cache"
        );
        warehouseService.current.clearWarehouseCache(product.accountId);
      }

      // Fetch active warehouses for the account
      const fetchedWarehouses =
        await warehouseService.current.getMarketplaceWarehouses(
          product.accountId,
          true // isActive = true
        );

      setWarehouses(fetchedWarehouses);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to load warehouses";
      setError(errorMessage);
      console.error("[StockUpdateModal] Error fetching warehouses:", err);
    } finally {
      setWarehouseLoading(false);
    }
  };

  // Fetch warehouses when modal opens
  useEffect(() => {
    if (product && isOpen) {
      fetchWarehouses();
    }
  }, [product, isOpen]);

  // Convert product data to variant stock format using real warehouses
  useEffect(() => {
    if (product && isOpen && warehouses.length > 0) {
      const variantStocks: VariantStock[] = [];

      if (
        product.variantCombinations &&
        product.variantCombinations.length > 0
      ) {
        // Multi-variant product
        product.variantCombinations
          .filter((variant) => variant.isActive)
          .forEach((variant) => {
            // Filter out warehouses without marketplace_warehouse_id
            const validWarehouses = warehouses.filter(
              (wh) => wh.marketplace_warehouse_id
            );

            const warehouseStocks: WarehouseStock[] = validWarehouses.map(
              (wh) => {
                const currentStock =
                  variant.inventoryAllocation?.[wh.marketplace_warehouse_id] ||
                  0;
                return {
                  warehouseId: wh.marketplace_warehouse_id,
                  warehouseName:
                    wh.marketplace_warehouse_name ||
                    wh.marketplace_warehouse_id,
                  currentStock: currentStock,
                  readyStock: currentStock,
                };
              }
            );

            variantStocks.push({
              variantId: variant.variantId,
              variantName: formatVariantName(variant.tierVariation, true),
              sku: variant.sku,
              merchantSku: variant.merchantProduct?.sku || undefined,
              marketplaceSku: variant.sku,
              image: variant.photo || product.mainImage,
              warehouses: warehouseStocks,
            });
          });
      } else {
        // Single variant product
        const validWarehouses = warehouses.filter(
          (wh) => wh.marketplace_warehouse_id
        );

        const warehouseStocks: WarehouseStock[] = validWarehouses.map((wh) => {
          const currentStock =
            product.inventoryAllocation?.[wh.marketplace_warehouse_id] || 0;
          return {
            warehouseId: wh.marketplace_warehouse_id,
            warehouseName:
              wh.marketplace_warehouse_name || wh.marketplace_warehouse_id,
            currentStock: currentStock,
            readyStock: currentStock,
          };
        });

        variantStocks.push({
          variantId: "main",
          variantName: product.title,
          sku: product.sku,
          merchantSku: product.merchantProduct?.sku || undefined,
          marketplaceSku: product.sku,
          image: product.mainImage,
          warehouses: warehouseStocks,
        });
      }

      setVariants(variantStocks);
    }
  }, [product, isOpen, warehouses]);

  const handleBatchUpdate = (stockValue: number) => {
    setVariants((prev) =>
      prev.map((variant) => ({
        ...variant,
        warehouses: variant.warehouses.map((warehouse) => ({
          ...warehouse,
          readyStock: stockValue,
        })),
      }))
    );
  };

  const handleStockChange = (
    variantId: string,
    warehouseId: string,
    newStock: number
  ) => {
    setVariants((prev) =>
      prev.map((variant) =>
        variant.variantId === variantId
          ? {
              ...variant,
              warehouses: variant.warehouses.map((warehouse) =>
                warehouse.warehouseId === warehouseId
                  ? { ...warehouse, readyStock: newStock }
                  : warehouse
              ),
            }
          : variant
      )
    );
  };

  const handleSave = async () => {
    if (!product) return;

    // Clear warehouse cache to ensure fresh data on next load
    console.log(
      "[StockUpdateModal] Clearing warehouse cache for account:",
      product.accountId
    );
    warehouseService.current.clearWarehouseCache(product.accountId);

    try {
      setLoading(true);
      setError(null);

      // Debug logging: Log all data before validation
      console.log("[StockUpdateModal] === DEBUG: Product Data ===");
      console.log("[StockUpdateModal] Product SKU:", product.sku);
      console.log("[StockUpdateModal] Account ID:", product.accountId);
      console.log("[StockUpdateModal] Variant Count:", variants.length);

      console.log("[StockUpdateModal] === DEBUG: Variants Data ===");
      variants.forEach((variant, idx) => {
        console.log(`[StockUpdateModal] Variant ${idx + 1}:`, {
          variantId: variant.variantId,
          variantName: variant.variantName,
          sku: variant.sku,
          warehouseCount: variant.warehouses.length,
        });
      });

      console.log("[StockUpdateModal] === DEBUG: Warehouse Data ===");
      variants.forEach((variant) => {
        variant.warehouses.forEach((warehouse, idx) => {
          console.log(
            `[StockUpdateModal] ${variant.variantName} - Warehouse ${idx + 1}:`,
            {
              warehouseId: warehouse.warehouseId,
              warehouseName: warehouse.warehouseName,
              currentStock: warehouse.currentStock,
              readyStock: warehouse.readyStock,
            }
          );
        });
      });

      // Transform to API format - only include updates with changes
      const stockUpdates: StockUpdateItem[] = [];

      variants.forEach((variant) => {
        variant.warehouses.forEach((warehouse) => {
          // Debug: Log types and values for comparison
          console.log("[StockUpdateModal] Stock comparison:", {
            productSku: product.sku,
            productListingId: product.productListingId,
            variantSku: variant.sku,
            variantId: variant.variantId,
            warehouseId: warehouse.warehouseId,
            currentStock: warehouse.currentStock,
            currentStockType: typeof warehouse.currentStock,
            readyStock: warehouse.readyStock,
            readyStockType: typeof warehouse.readyStock,
            isChanged: warehouse.readyStock !== warehouse.currentStock,
          });
          // Only include warehouses where stock changed
          if (warehouse.readyStock !== warehouse.currentStock) {
            const updateItem: StockUpdateItem = {
              warehouse_id: warehouse.warehouseId,
              new_ready_stock: warehouse.readyStock,
            };

            // 1. If product has sku, use sku; otherwise use listing_id
            if (product.sku) {
              updateItem.sku = product.sku;
            } else {
              updateItem.listing_id = product.productListingId;
            }

            // 2. Only add variant fields if this is a multi-variant product (not "main")
            if (variant.variantId !== "main") {
              if (variant.sku) {
                updateItem.variant_sku = variant.sku;
              } else {
                updateItem.listing_variant_id = variant.variantId;
              }
            }

            stockUpdates.push(updateItem);
          }
        });
      });

      console.log("[StockUpdateModal] === DEBUG: Stock Updates Payload ===");
      console.log("[StockUpdateModal] Total updates:", stockUpdates.length);
      stockUpdates.forEach((update, idx) => {
        console.log(`[StockUpdateModal] Update ${idx + 1}:`, update);
      });

      // Validation: Check if there are any changes
      if (stockUpdates.length === 0) {
        setError("No stock changes detected");
        setLoading(false);
        return;
      }

      // Validation: Check for missing required fields
      const invalidUpdates = stockUpdates.filter((update) => {
        console.log(`wh ${!update.warehouse_id}`);
        console.log(`parent ${!(update.sku ?? update.listing_id)}`);
        console.log(
          `variant ${!(update.variant_sku ?? update.listing_variant_id)}`
        );

        return (
          !update.warehouse_id || //false
          (!(update.sku ?? update.listing_id) && //false
            !(update.variant_sku ?? update.listing_variant_id))
          // !update.sku ||
          // !update.listing_id ||
          // !update.variant_sku ||
          // !update.listing_variant_id ||
          // !update.warehouse_id
        );
      });

      if (invalidUpdates.length > 0) {
        console.error(
          "[StockUpdateModal] Invalid updates found:",
          invalidUpdates
        );
        console.error("[StockUpdateModal] Product SKU:", product.sku);
        console.error(
          "[StockUpdateModal] All variants:",
          variants.map((v) => ({
            sku: v.sku,
            variantId: v.variantId,
            warehouses: v.warehouses.map((w) => ({ id: w.warehouseId })),
          }))
        );
        setError(
          `Cannot update stock: ${invalidUpdates.length} item(s) missing required data. Check console for details.`
        );
        setLoading(false);
        return;
      }

      // Call bulk stock update API
      console.log("[StockUpdateModal] === API REQUEST ===");
      console.log("[StockUpdateModal] Endpoint: product_listings/stock");
      console.log("[StockUpdateModal] Account ID:", product.accountId);
      console.log(
        "[StockUpdateModal] Request payload:",
        JSON.stringify(stockUpdates, null, 2)
      );

      const response = await productListingService.current.updateProductStock(
        product.accountId,
        stockUpdates
      );

      // CRITICAL: Clear product listing cache so UI shows fresh data
      // Server updates database BEFORE responding, so cache must be invalidated immediately
      productListingService.current.clearProductListingCache(product.accountId);

      // Start process tracking
      setProcessId(response.process_id);
      setIsProcessing(true);

      console.log("[StockUpdateModal] Stock update initiated:", response);

      // Show success toast
      toast(
        `Stock update submitted successfully! Updated ${response.total_products} product(s). Marketplace sync queued.`,
        {
          type: "success",
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        }
      );

      // Close modal after successful submission
      onClose();

      // Trigger parent refresh (ProductListingPage will refresh the product list)
      // The onSave callback is kept for this purpose, but we pass empty array
      // since the actual API call is already done above
      await onSave([]);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : "Failed to update stock";
      setError(errorMessage);

      // Show error toast
      toast(errorMessage, {
        type: "error",
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setError(null);
    onClose();
  };

  if (!isOpen || !product) return null;

  // Render mobile view on small screens
  if (isMobile) {
    return (
      <MobileStockUpdateView
        product={product}
        variants={variants}
        onStockChange={handleStockChange}
        onSave={handleSave}
        onCancel={handleCancel}
        loading={loading || warehouseLoading}
        error={error}
      />
    );
  }

  // Collect all unique merchant SKUs from variants
  const merchantSkus = variants
    .map((v) => v.merchantSku)
    .filter(
      (sku): sku is string => sku !== undefined && sku !== null && sku !== "N/A"
    )
    .filter((sku, index, self) => self.indexOf(sku) === index);

  const productHeaderCard = (
    <>
      {/* Left side: Image + Title + Product info */}
      <div className="tw-flex tw-items-center tw-gap-4">
        <img
          alt="Product image"
          className="tw-h-14 tw-w-14 tw-rounded-lg tw-object-cover tw-shrink-0"
          src={
            product.mainImage ||
            "https://via.placeholder.com/56x56?text=No+Image"
          }
        />
        <div className="tw-flex tw-flex-col">
          {/* Title beside image */}
          <h2 className="tw-text-xl tw-font-bold tw-text-gray-900">
            Update Stock
          </h2>
          {/* Product info row */}
          <div className="tw-flex tw-items-center tw-gap-4 tw-text-sm tw-text-gray-500 tw-mt-1">
            <p>
              <span className="tw-font-medium tw-text-gray-700">Product:</span>{" "}
              {product.title}
            </p>
            <div className="tw-h-4 tw-border-l tw-border-gray-300"></div>
            <p>
              <span className="tw-font-medium tw-text-gray-700">Store:</span>{" "}
              {stores.find((s) => s.id === product.accountId)?.name ||
                product.marketplace.charAt(0).toUpperCase() +
                  product.marketplace.slice(1)}
            </p>
          </div>
        </div>
      </div>

      {/* Right side: Close button */}
      <button
        onClick={handleCancel}
        className="tw-p-1.5 tw-rounded-full hover:tw-bg-gray-100 tw-text-gray-600 tw-transition-colors"
        aria-label="Close modal"
        data-testid="close-button"
      >
        <span className="material-symbols-outlined tw-text-2xl">close</span>
      </button>
    </>
  );

  const footerContent = (
    <div className="tw-flex tw-flex-col tw-gap-3">
      {error && (
        <div
          className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-p-3 tw-flex tw-items-center tw-justify-between"
          data-testid="stock-modal-footer-error"
        >
          <p className="tw-text-red-800 tw-text-sm tw-flex-1">{error}</p>
          <button
            onClick={() => fetchWarehouses(true)}
            className="tw-ml-3 tw-text-red-600 hover:tw-text-red-800 tw-text-sm tw-font-medium tw-underline tw-flex-shrink-0"
            data-testid="stock-modal-retry-button"
          >
            Retry
          </button>
        </div>
      )}
      <div className="tw-flex tw-justify-end tw-gap-3">
        <button
          onClick={handleCancel}
          className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-10 tw-px-4 tw-bg-white tw-text-gray-800 tw-text-sm tw-font-bold hover:tw-bg-gray-100 tw-border tw-border-gray-300"
          disabled={loading || warehouseLoading}
          data-testid="stock-modal-cancel-button"
        >
          <span className="tw-truncate">Cancel</span>
        </button>
        <button
          onClick={handleSave}
          disabled={loading || warehouseLoading || variants.length === 0}
          className="tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-h-10 tw-px-4 tw-bg-primary tw-text-white tw-text-sm tw-font-bold hover:tw-bg-primary/90 disabled:tw-bg-primary/40 disabled:tw-cursor-not-allowed"
          data-testid="stock-modal-save-button"
        >
          <span className="tw-truncate">
            {loading ? "Saving..." : "Save Changes"}
          </span>
        </button>
      </div>
    </div>
  );

  const contentArea = (
    <div className="tw-flex-1 tw-overflow-y-auto tw-p-6 tw-space-y-6">
      {warehouseLoading ? (
        <div
          className="tw-flex tw-items-center tw-justify-center tw-p-8"
          data-testid="stock-modal-warehouse-loading"
        >
          <div className="tw-animate-spin tw-rounded-full tw-h-8 tw-w-8 tw-border-b-2 tw-border-blue-600"></div>
          <span className="tw-ml-3 tw-text-gray-600">
            Loading warehouses...
          </span>
        </div>
      ) : (
        <>
          <BatchStockUpdate onApply={handleBatchUpdate} />

          <DesktopStockUpdateView
            variants={variants}
            onStockChange={handleStockChange}
          />
        </>
      )}
    </div>
  );

  if (mode === "content") {
    return (
      <ContentModalContainer
        isOpen={isOpen}
        onClose={handleCancel}
        title="Update Stock"
        customHeaderContent={productHeaderCard}
        footer={footerContent}
      >
        {contentArea}
      </ContentModalContainer>
    );
  }

  return (
    <ModalContainer
      isOpen={isOpen}
      onClose={handleCancel}
      title="Update Stock"
      customHeaderContent={productHeaderCard}
      hideDefaultTitle={true}
      size="6xl"
      // className="!tw-max-w-5xl"
      titleClassName="!tw-font-bold"
      footerClassName="!tw-p-4 !tw-bg-gray-50"
      fullscreenOverlay={true}
      footer={footerContent}
    >
      {contentArea}
    </ModalContainer>
  );
};

export default StockUpdateModal;
